import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '../cn.js';
import { useUiLabels } from '../labels.js';

/** A calendar day as `YYYY-MM-DD` (local date, no time zone). */
export type IsoDate = string;

export interface DatePickerProps {
  value: IsoDate | null;
  onChange: (value: IsoDate | null) => void;
  id?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  disabled?: boolean;
  /** Earliest / latest selectable day. */
  min?: IsoDate;
  max?: IsoDate;
  /** Shows a "clear" action (the value can be null). */
  clearable?: boolean;
  /** Shortcuts under the calendar, e.g. "Tomorrow", "In 1 week". */
  shortcuts?: { label: string; value: IsoDate }[];
  /** Text of the button for a value; defaults to a medium date in the labels' locale. */
  formatValue?: (value: IsoDate) => ReactNode;
  placeholder?: string;
  /** Same looks as Select: `default` like an input, `subtle` for toolbars, `ghost` for rows. */
  variant?: 'default' | 'subtle' | 'ghost';
  size?: 'sm' | 'md';
  /** Hides the calendar icon in the button. */
  hideIcon?: boolean;
  className?: string;
}

const triggerVariants = {
  default:
    'border border-border bg-surface shadow-panel hover:border-border-strong focus-visible:border-accent focus-visible:ring-4 focus-visible:ring-accent/15 aria-[invalid=true]:border-danger',
  subtle:
    'bg-surface-2 ring-1 ring-inset ring-border hover:bg-surface-3/60 focus-visible:ring-accent',
  ghost: 'font-medium hover:bg-surface-2 focus-visible:bg-surface-2 aria-expanded:bg-surface-2',
};

const GAP = 4;
const PANEL_WIDTH = 288;

const pad = (n: number) => String(n).padStart(2, '0');
export const toIsoDate = (date: Date): IsoDate =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
export function parseIsoDate(value: IsoDate): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}
const addDays = (date: Date, days: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const addMonths = (date: Date, months: number) => {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), last));
};

/** Runs a DOM call that older engines (and jsdom) may lack. */
function tryDom<T>(call: () => T, fallback: T): T {
  try {
    return call();
  } catch {
    return fallback;
  }
}

/** First day of the week for a locale (Monday unless the locale says otherwise). */
function weekStart(locale: string): number {
  const info = tryDom(
    () => (new Intl.Locale(locale) as Intl.Locale & { weekInfo?: { firstDay: number } }).weekInfo,
    undefined,
  );
  return info ? info.firstDay % 7 : 1;
}

/**
 * Themed date picker (the native date input ignores the app's theme and language). The calendar
 * is a manual popover in the top layer, like Select's list, so it works inside modal dialogs.
 * Keyboard: arrows move by day/week, PageUp/PageDown by month, Home/End to the week's ends,
 * Enter picks, Esc closes.
 */
export function DatePicker({
  value,
  onChange,
  id,
  disabled,
  min,
  max,
  clearable,
  shortcuts,
  formatValue,
  placeholder,
  variant = 'default',
  size = 'md',
  hideIcon,
  className,
  ...aria
}: DatePickerProps) {
  const labels = useUiLabels();
  const locale = labels.locale;
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState<Date>(() => (value ? parseIsoDate(value) : new Date()));
  const [position, setPosition] = useState<{ top: number; left: number }>();

  const today = toIsoDate(new Date());
  const firstDay = useMemo(() => weekStart(locale), [locale]);
  const monthTitle = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
    [locale],
  );
  const dayLabel = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }),
    [locale],
  );
  const display = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
    [locale],
  );
  const weekdays = useMemo(() => {
    const narrow = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // 2024-01-07 was a Sunday.
    return Array.from({ length: 7 }, (_, i) =>
      narrow.format(new Date(2024, 0, 7 + ((firstDay + i) % 7))),
    );
  }, [locale, firstDay]);

  const outOfRange = (iso: IsoDate) =>
    (min !== undefined && iso < min) || (max !== undefined && iso > max);

  // 6 weeks starting on the locale's first weekday on or before the 1st of the month.
  const days = useMemo(() => {
    const first = new Date(focused.getFullYear(), focused.getMonth(), 1);
    const start = addDays(first, -((first.getDay() - firstDay + 7) % 7));
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [focused, firstDay]);

  function show() {
    if (disabled) return;
    setFocused(value ? parseIsoDate(value) : new Date());
    setOpen(true);
  }

  function hide(refocus = true) {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }

  function pick(next: IsoDate | null) {
    if (next !== null && outOfRange(next)) return;
    if (next !== value) onChange(next);
    hide();
  }

  // Place the calendar under the button (above it when there is no room).
  useLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const height = panel?.offsetHeight ?? 340;
      const below = window.innerHeight - rect.bottom;
      const top =
        below < height + GAP && rect.top > below
          ? Math.max(GAP, rect.top - height - GAP)
          : rect.bottom + GAP;
      const left = Math.min(rect.left, window.innerWidth - PANEL_WIDTH - GAP);
      setPosition({ top, left: Math.max(GAP, left) });
    };
    if (panel && !tryDom(() => panel.matches(':popover-open'), false)) {
      tryDom(() => panel.showPopover(), undefined);
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      if (panel && tryDom(() => panel.matches(':popover-open'), false)) {
        tryDom(() => panel.hidePopover(), undefined);
      }
    };
  }, [open]);

  // Close on a click anywhere else.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) hide(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  // Keyboard focus follows the focused day inside the grid.
  useEffect(() => {
    if (!open) return;
    const cell = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-date="${toIsoDate(focused)}"]`,
    );
    cell?.focus({ preventScroll: true });
  }, [open, focused]);

  function onGridKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const moves: Record<string, () => Date> = {
      ArrowLeft: () => addDays(focused, -1),
      ArrowRight: () => addDays(focused, 1),
      ArrowUp: () => addDays(focused, -7),
      ArrowDown: () => addDays(focused, 7),
      PageUp: () => addMonths(focused, -1),
      PageDown: () => addMonths(focused, 1),
      Home: () => addDays(focused, -((focused.getDay() - firstDay + 7) % 7)),
      End: () => addDays(focused, 6 - ((focused.getDay() - firstDay + 7) % 7)),
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      setFocused(move());
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      pick(toIsoDate(focused));
    }
  }

  function onPanelKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      // Close only the calendar, not the dialog around it.
      event.preventDefault();
      event.stopPropagation();
      hide();
    } else if (event.key === 'Tab' && !panelRef.current?.contains(document.activeElement)) {
      hide(false);
    }
  }

  const footerShortcuts = shortcuts ?? [{ label: labels.today, value: today }];
  const text = value ? (formatValue?.(value) ?? display.format(parseIsoDate(value))) : null;
  const navButton =
    'flex size-7 items-center justify-center rounded-lg text-muted hover:bg-surface-2 hover:text-text';
  const chip =
    'h-7 rounded-lg px-2.5 text-xs font-medium ring-1 ring-inset ring-border hover:bg-surface-2';

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={aria['aria-label']}
        aria-invalid={aria['aria-invalid']}
        aria-describedby={aria['aria-describedby']}
        disabled={disabled}
        onClick={() => (open ? hide() : show())}
        onKeyDown={(event) => {
          if (!open && event.key === 'ArrowDown') {
            event.preventDefault();
            show();
          }
        }}
        className={cn(
          'inline-flex min-w-0 items-center gap-2 rounded-lg text-left text-sm text-text transition-[background-color,border-color,box-shadow] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
          size === 'sm' ? 'h-8 px-2.5 text-[13px]' : 'h-9 px-3',
          triggerVariants[variant],
          variant === 'ghost' ? 'max-w-full px-2' : 'w-full',
          className,
        )}
      >
        {!hideIcon && <CalendarDays aria-hidden className="size-4 shrink-0 text-subtle" />}
        <span
          className={cn(
            'min-w-0 truncate',
            variant !== 'ghost' && 'flex-1',
            !text && 'text-subtle',
          )}
        >
          {text ?? placeholder ?? labels.noDate}
        </span>
      </button>
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label={aria['aria-label']}
          popover="manual"
          onKeyDown={onPanelKeyDown}
          style={position && { top: position.top, left: position.left, width: PANEL_WIDTH }}
          className={cn(
            'fixed inset-auto m-0 animate-fade-in rounded-xl border border-border bg-surface p-3 text-sm text-text shadow-float',
            !position && 'invisible',
          )}
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              aria-label={labels.previousMonth}
              onClick={() => setFocused(addMonths(focused, -1))}
              className={navButton}
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="font-semibold first-letter:uppercase" aria-live="polite">
              {monthTitle.format(focused)}
            </span>
            <button
              type="button"
              aria-label={labels.nextMonth}
              onClick={() => setFocused(addMonths(focused, 1))}
              className={navButton}
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
          <div
            ref={gridRef}
            role="grid"
            onKeyDown={onGridKeyDown}
            className="grid grid-cols-7 gap-0.5 text-center"
          >
            {weekdays.map((day) => (
              <span
                key={day}
                role="columnheader"
                className="pb-1 text-[11px] font-medium text-subtle uppercase"
              >
                {day.replace('.', '')}
              </span>
            ))}
            {days.map((day) => {
              const iso = toIsoDate(day);
              const inMonth = day.getMonth() === focused.getMonth();
              const isSelected = iso === value;
              const isFocused = iso === toIsoDate(focused);
              const disabledDay = outOfRange(iso);
              return (
                <button
                  key={iso}
                  type="button"
                  role="gridcell"
                  data-date={iso}
                  tabIndex={isFocused ? 0 : -1}
                  aria-selected={isSelected}
                  aria-label={dayLabel.format(day)}
                  aria-current={iso === today ? 'date' : undefined}
                  disabled={disabledDay}
                  onClick={() => pick(iso)}
                  className={cn(
                    'flex h-8 items-center justify-center rounded-lg text-[13px] tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                    inMonth ? 'text-text' : 'text-subtle/70',
                    !isSelected && 'hover:bg-surface-2',
                    iso === today &&
                      !isSelected &&
                      'font-semibold text-accent ring-1 ring-inset ring-accent/40',
                    isSelected && 'bg-accent font-semibold text-accent-fg',
                    disabledDay && 'cursor-not-allowed opacity-30 hover:bg-transparent',
                  )}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          {footerShortcuts.length + (clearable && value ? 1 : 0) > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border pt-3">
              {footerShortcuts.map((shortcut) => (
                <button
                  key={shortcut.label}
                  type="button"
                  disabled={outOfRange(shortcut.value)}
                  onClick={() => pick(shortcut.value)}
                  className={cn(
                    chip,
                    shortcut.value === value
                      ? 'bg-accent-soft text-accent ring-accent/30'
                      : 'text-muted',
                    'disabled:cursor-not-allowed disabled:opacity-40',
                  )}
                >
                  {shortcut.label}
                </button>
              ))}
              {clearable && value && (
                <button
                  type="button"
                  onClick={() => pick(null)}
                  className="ml-auto inline-flex h-7 items-center gap-1 rounded-lg px-2 text-xs text-subtle hover:text-danger"
                >
                  <X className="size-3.5" />
                  {labels.clear}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
